/* eslint-disable mobx/missing-make-observable */
import { computed, makeAutoObservable, observable } from "mobx";
import type { ImperativeModalProps } from "@web/src/app/entities/imperative-modal";

import type { SpaceId } from "@common/types/space";
import tailwindConfig from "@web/tailwind.config.js";
import moment from "moment";
import type HighlightStore from "./highlight-store";
import type { TodoId } from "@common/types/todo";
import type UserStore from "@common/stores/user-store";
import type { CommonEvents } from "@common/services/common-events";
import type { HighlightableId } from "@web/src/app/use-cases/todo-highlight";

interface UIStoreDependencies {
  highlightStore: HighlightStore;
  userStore: UserStore;
  commonEvents: typeof CommonEvents;
}
export default class UIStore {
  noteEditorWidthFraction = 0.5;
  @observable
  deps: UIStoreDependencies;

  @computed
  getIsFocused() {
    if (this.deps.highlightStore.currentHighlightObj?.kind !== "todo") return false;
    return Boolean(
      this.focusedHighlightId && this.deps.highlightStore.currentHighlightObj.id === this.focusedHighlightId
    );
  }

  @observable
  focusedHighlightId: HighlightableId | null = null;

  setFocusedTodoId(id: HighlightableId | null) {
    this.focusedHighlightId = id;
  }

  debugMode = false;
  isTodayCompletedExpanded = false;
  isHistoryExpanded = false;

  colors = {
    primary: "#2E3A59",
    text: tailwindConfig.theme.extend.colors["glyph-prm"],
    secondaryText: tailwindConfig.theme.extend.colors["glyph-scd"],
    contrastText: tailwindConfig.theme.extend.colors["glyph-cst-prm"],
    highlightText: tailwindConfig.theme.extend.colors["blue-3"],
    background: tailwindConfig.theme.extend.colors["surface-prm"],
    backgroundSecondary: tailwindConfig.theme.extend.colors["surface-scd"],
    highlight: tailwindConfig.theme.extend.colors["green-3"],
    positive: tailwindConfig.theme.extend.colors["green-3"], // 00BF36
    negative: tailwindConfig.theme.extend.colors["red-3"],
    warning: "#DB9000",
    backgroundContrast: tailwindConfig.theme.extend.colors["surface-cst"],
    backgroundHighlight: tailwindConfig.theme.extend.colors["blue-3"],
  };

  isolatedTodoId: TodoId | null = null;

  constructor(deps: UIStoreDependencies) {
    makeAutoObservable(this);
    this.deps = deps;
  }

  loadSettings() {
    if (typeof window !== "undefined") {
      this.isTodayCompletedExpanded = (localStorage.getItem("today-completed-expanded") ?? "true") == "true";

      // Load collapsed groups from localStorage
      const savedCollapsedGroups = localStorage.getItem("collapsed-groups");
      if (savedCollapsedGroups) {
        try {
          this.collapsedGroups = JSON.parse(savedCollapsedGroups);
        } catch (e) {
          // If parsing fails, start with empty state
          this.collapsedGroups = {};
        }
      }
    }
  }

  modal?: ImperativeModalProps;

  @computed
  get hasModal() {
    return this.modal !== undefined;
  }

  setModalProps(modalProps: typeof this.modal) {
    this.modal = modalProps;
  }

  setDebugMode(mode: boolean) {
    this.debugMode = mode;
  }

  toggleIsTodayCompletedExpanded() {
    this.isTodayCompletedExpanded = !this.isTodayCompletedExpanded;
    localStorage.setItem("today-completed-expanded", String(this.isTodayCompletedExpanded));
  }

  toggleIsHistoryExpanded() {
    if (!this.isHistoryExpanded && !this.deps.userStore.isPremium) {
      this.deps.commonEvents.emit("PAYWALL", { from: "history" });
      return;
    }
    this.isHistoryExpanded = !this.isHistoryExpanded;
  }

  setIsolatedTodoId(id: string | null) {
    this.isolatedTodoId = id;
  }

  collapsedGroups: Record<SpaceId, Record<string, boolean>> = {};

  toggleCollapseSubgroup(spaceId: string, date: number | null) {
    const dateStr = date === null ? "someday" : moment(date).format("YYYY-MM-DD");
    this.collapsedGroups[spaceId] ??= {};
    this.collapsedGroups[spaceId][dateStr] = !this.collapsedGroups[spaceId][dateStr];

    // Save to localStorage
    if (typeof window !== "undefined") {
      localStorage.setItem("collapsed-groups", JSON.stringify(this.collapsedGroups));
    }
  }

  isGroupedCollapsed(spaceId: string, date: number | null) {
    const dateStr = date === null ? "someday" : moment(date).format("YYYY-MM-DD");
    const isGroupCollased = this.collapsedGroups[spaceId]?.[dateStr];
    return Boolean(isGroupCollased);
  }
}
